
import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n';

export type Language = 'tr' | 'krm' | 'en' | 'ar';

export const normalizeLanguage = (lang?: string | null): Language => {
    if (!lang) return 'tr';
    const clean = lang.toLowerCase().split(/[-_]/)[0];
    if (clean === 'ku' || clean === 'kmr' || clean === 'krm') return 'krm';
    if (clean === 'en') return 'en';
    if (clean === 'ar') return 'ar';
    return 'tr';
};

export const languageToStrapiLocale = (lang?: string | null): string => {
    const normalized = normalizeLanguage(lang);
    if (normalized === 'krm') return 'kmr';
    return normalized;
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language | string) => void;
    locale: string;
    alternatePaths: Record<string, string> | null;
    setAlternatePaths: (paths: Record<string, string> | null) => void;
    isLoadingContent: boolean;
    setIsLoadingContent: (loading: boolean) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        const saved = localStorage.getItem('i18nextLng');
        const normalized = normalizeLanguage(saved);
        if (saved !== normalized) {
            localStorage.setItem('i18nextLng', normalized);
        }
        return normalized;
    });
    const [alternatePaths, setAlternatePaths] = useState<Record<string, string> | null>(null);
    const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false);

    const setLanguage = (lang: Language | string) => {
        const normalized = normalizeLanguage(lang);
        i18n.changeLanguage(normalized);
        setLanguageState(normalized);
        localStorage.setItem('i18nextLng', normalized);
    };

    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            const normalized = normalizeLanguage(lng);
            setLanguageState(normalized);
            localStorage.setItem('i18nextLng', normalized);
        };
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    useEffect(() => {
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
        if (language === 'ar') {
            document.body.classList.add('font-arabic');
        } else {
            document.body.classList.remove('font-arabic');
        }
    }, [language]);

    // Map app language to Strapi locale codes (tr -> tr, krm -> kmr, en -> en, ar -> ar)
    const locale = languageToStrapiLocale(language);

    return (
        <LanguageContext.Provider value={{ language, setLanguage, locale, alternatePaths, setAlternatePaths, isLoadingContent, setIsLoadingContent }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
